import { useState } from 'react'
import AgentResponseReferencesGroup from './AgentResponseReferencesGroup'
import { extractCitationIds } from '@/util/agent-citations'
import {
  buildAgentReferenceDisplayGroups,
  FootnoteInfoByCitationId,
  getCitationIdsOutsideCitedReferenceGroups,
} from '@/agent/references/util/agent-reference-footnotes'
import { useAppSelector } from '@/store/store-hooks'

type AgentResponseReferencesSectionProps = {
  response_message: string
  footnoteInfoByCitationId: FootnoteInfoByCitationId
}

export default function AgentResponseReferencesSection(props: AgentResponseReferencesSectionProps) {
  const { response_message, footnoteInfoByCitationId } = props
  const [expandReferences, setExpandReferences] = useState(false)
  const [expandRelated, setExpandRelated] = useState(false)

  const turnUniqueCitationIds = Object.keys(footnoteInfoByCitationId)
  const referencesByCitationId = useAppSelector((state) => state.agentReferencesState.references)

  // No citations registered for this turn — nothing to render.
  if (turnUniqueCitationIds.length === 0) return null

  // Identify the citation_ids in the response_message
  const matchedCitationIds = extractCitationIds(response_message)

  // Filter the turnUniqueCitationIds to only include the citation_ids in the response_message
  const usedCitationIds = matchedCitationIds.filter((citation_id) => turnUniqueCitationIds.includes(citation_id))
  const unusedCitationIds = getCitationIdsOutsideCitedReferenceGroups(turnUniqueCitationIds, usedCitationIds, referencesByCitationId)

  const citedReferenceGroups = buildAgentReferenceDisplayGroups(usedCitationIds, footnoteInfoByCitationId, referencesByCitationId)
  const relatedReferenceGroups = buildAgentReferenceDisplayGroups(unusedCitationIds, footnoteInfoByCitationId, referencesByCitationId)

  const citedTitle = citedReferenceGroups.length === 1 ? 'cited reference' : 'cited references'
  const relatedTitle = relatedReferenceGroups.length === 1 ? 'related reference' : 'related references'

  // Show IF:
  // - expandReferences (top level) is true AND there are related references
  // - cited references are empty AND there are related references
  //     - This is because there will be no UX to set expanded
  const showUnusedCitations =
    (expandReferences && relatedReferenceGroups.length > 0) || (citedReferenceGroups.length === 0 && relatedReferenceGroups.length > 0)

  return (
    <div className="mt-2">
      {citedReferenceGroups.length > 0 && (
        <AgentResponseReferencesGroup
          title={citedTitle}
          numOfReferences={citedReferenceGroups.length}
          referenceGroups={citedReferenceGroups}
          isExpanded={expandReferences}
          toggleExpand={() => setExpandReferences(!expandReferences)}
        />
      )}

      {showUnusedCitations && (
        <AgentResponseReferencesGroup
          title={relatedTitle}
          numOfReferences={relatedReferenceGroups.length}
          referenceGroups={relatedReferenceGroups}
          isExpanded={expandRelated}
          toggleExpand={() => setExpandRelated(!expandRelated)}
        />
      )}
    </div>
  )
}
